import { useCallback, useMemo } from 'react';
import { MapEngine } from '../types';

const KEY_PREFIX = 'hybridMap:engine:';
const DEFAULT_TTL_MS = 60 * 60 * 1000; // 1h

interface PersistedEntry {
    engine: MapEngine;
    savedAt: number;
}

interface UseMapEnginePersistenceOptions {
    persistKey?: string;
    persistTtlMs?: number;
}

interface UseMapEnginePersistenceResult {
    read: () => MapEngine | null;
    write: (engine: MapEngine) => void;
    clear: () => void;
}

const isValidEngine = (value: unknown): value is MapEngine => value === 'mapbox' || value === 'google';

export const useMapEnginePersistence = ({
    persistKey,
    persistTtlMs = DEFAULT_TTL_MS,
}: UseMapEnginePersistenceOptions): UseMapEnginePersistenceResult => {
    const storageKey = persistKey ? `${KEY_PREFIX}${persistKey}` : null;

    const read = useCallback((): MapEngine | null => {
        if (!storageKey) return null;

        try {
            const raw = window.localStorage.getItem(storageKey);

            if (!raw) return null;

            const parsed = JSON.parse(raw) as PersistedEntry;

            if (!parsed || !isValidEngine(parsed.engine)) return null;

            if (persistTtlMs > 0 && Date.now() - parsed.savedAt > persistTtlMs) {
                window.localStorage.removeItem(storageKey);

                return null;
            }

            return parsed.engine;
        } catch {
            return null;
        }
    }, [storageKey, persistTtlMs]);

    const write = useCallback((engine: MapEngine) => {
        if (!storageKey) return;

        try {
            const entry: PersistedEntry = { engine, savedAt: Date.now() };

            window.localStorage.setItem(storageKey, JSON.stringify(entry));
        } catch {
            // localStorage unavailable (private mode, quota exceeded). Silent degradation.
        }
    }, [storageKey]);

    const clear = useCallback(() => {
        if (!storageKey) return;

        try {
            window.localStorage.removeItem(storageKey);
        } catch {
            // noop
        }
    }, [storageKey]);

    return useMemo(() => ({ read, write, clear }), [read, write, clear]);
};
